// API client configuration and TanStack Query integration
import { ApiClient } from "./generated/api-client";
import { getConfig, shouldUseMockAuth } from "../config/runtimeConfig";
import { mockAuthService } from "../auth/mockAuth";
import { isE2ETestMode, getE2EAccessToken } from "../auth/e2eAuth";
import { resetAuthRecoveryCounter } from "../auth/authRecovery";

/**
 * Global toast handler for API errors
 * This will be set by the App component after ToastProvider is initialized
 */
let globalToastHandler: ((title: string, message?: string) => void) | null =
  null;

/**
 * Token result returned by the global token provider, including the actual expiry
 * so the client cache can align with the real token lifetime.
 */
export interface TokenResult {
  token: string;
  expiresOn: Date | null;
}

/**
 * Global token provider for API client
 * This will be set by the App component after MSAL is initialized
 */
let globalTokenProvider: ((forceRefresh?: boolean) => Promise<TokenResult | null>) | null = null;

/**
 * Global authentication redirect handler
 * This will be set by the App component to handle automatic login redirects
 */
let globalAuthRedirectHandler: (() => void) | null = null;

/**
 * Token cache to avoid requesting new tokens for every API call
 */
interface TokenCache {
  token: string;
  expiresAt: number; // Unix timestamp
}

let tokenCache: TokenCache | null = null;

/**
 * Check if cached token is still valid (with 5 minute buffer)
 */
const isCachedTokenValid = (): boolean => {
  if (!tokenCache) return false;
  const now = Date.now();
  const bufferMs = 5 * 60 * 1000; // 5 minutes buffer
  return tokenCache.expiresAt > now + bufferMs;
};

/**
 * Set the global token provider
 * This should be called from App component after MSAL initialization
 */
export const setGlobalTokenProvider = (
  provider: (forceRefresh?: boolean) => Promise<TokenResult | null>,
) => {
  globalTokenProvider = provider;
};

/**
 * Set the global authentication redirect handler
 * This should be called from App component to handle automatic login redirects on 401 errors
 */
export const setGlobalAuthRedirectHandler = (
  handler: () => void,
) => {
  globalAuthRedirectHandler = handler;
  console.log("🔐 Global auth redirect handler set for 401 errors");
};

/**
 * Set the global toast error handler
 * This should be called from App component after ToastProvider is initialized
 */
export const setGlobalToastHandler = (
  handler: (title: string, message?: string) => void,
) => {
  globalToastHandler = handler;
  console.log("🍞 Global toast handler set for API errors");
};

/**
 * Clear the token cache
 * This should be called during logout
 */
export const clearTokenCache = () => {
  tokenCache = null;
  console.log("🗑️ Token cache cleared");
};

/**
 * Centralized authentication header provider with token caching
 * According to documentation specification (Authentication.md section 7.1.3)
 */
const getAuthHeader = async (): Promise<string | null> => {
  if (isE2ETestMode()) {
    // E2E test mode - use session cookies, no Authorization header needed
    const token = getE2EAccessToken();
    if (token) {
      console.log("🧪 Using E2E authentication token for API call");
      return `Bearer ${token}`;
    } else {
      console.log(
        "🧪 E2E mode: Using session cookies, no Authorization header",
      );
      return null; // No Authorization header - backend validates via cookies
    }
  } else if (shouldUseMockAuth()) {
    // Mock authentication - use mockAuthService
    const token = mockAuthService.getAccessToken();
    console.log("🧪 Using cached mock authentication token for API call");
    return `Bearer ${token}`;
  } else {
    // Real authentication - check cache first
    if (isCachedTokenValid()) {
      console.log("⚡ Using cached authentication token for API call");
      return `Bearer ${tokenCache!.token}`;
    }

    // Cache miss or expired - get new token
    if (!globalTokenProvider) {
      console.error(
        "❌ Global token provider not set. Make sure App component initialized MSAL.",
      );
      return null;
    }

    try {
      console.log("🔐 Acquiring new authentication token...");
      const result = await globalTokenProvider();
      if (result) {
        const bufferMs = 5 * 60 * 1000; // 5 minutes safety buffer
        const expiresAt = result.expiresOn
          ? result.expiresOn.getTime() - bufferMs
          : Date.now() + 55 * 60 * 1000; // fallback: assume 1h token minus buffer
        tokenCache = { token: result.token, expiresAt };

        console.log("✅ New authentication token acquired and cached");
        return `Bearer ${result.token}`;
      } else {
        console.warn(
          "⚠️  No authentication token available - user may need to login",
        );
        return null;
      }
    } catch (error) {
      console.error("❌ Failed to acquire authentication token:", error);
      return null;
    }
  }
};

const TERMINAL_ROUTE_PREFIX = "/terminal";
const isTerminalRoute = (): boolean =>
  window.location.pathname.startsWith(TERMINAL_ROUTE_PREFIX);

// Create API client instance with runtime configuration
let apiClient: ApiClient;

export const getApiClient = (): ApiClient => {
  if (!apiClient) {
    const config = getConfig();
    console.log(`🌐 Creating API client with base URL: ${config.apiUrl}`);
    apiClient = new ApiClient(config.apiUrl);
  }
  return apiClient;
};

/**
 * Get the API base URL from runtime configuration
 */
export const getApiBaseUrl = (): string => {
  const config = getConfig();
  return config.apiUrl;
};

/**
 * Build authentication headers for API requests
 * Includes Authorization header, E2E test token, and Content-Type
 * NOTE: Content-Type is included here but may be overridden by FormData handling
 */
async function buildAuthHeaders(
  init?: RequestInit,
): Promise<Record<string, string>> {
  const authHeader = await getAuthHeader();

  const headers: Record<string, string> = {
    ...((init?.headers as Record<string, string>) || {}),
  };

  // Do not set Content-Type for FormData — browser sets it automatically with the correct multipart boundary
  if (!(init?.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  // Handle E2E authentication with special header
  if (isE2ETestMode()) {
    const e2eToken = getE2EAccessToken();
    if (e2eToken) {
      console.log("🧪 Setting X-E2E-Test-Token header for API call");
      headers["X-E2E-Test-Token"] = e2eToken;
    } else {
      console.log("🧪 E2E mode: Using session cookies, no special headers");
    }
  } else if (authHeader) {
    headers["Authorization"] = authHeader;
  }

  return headers;
}

/**
 * Extract error message from API response and check if it's a structured API error
 */
const extractErrorMessage = async (
  response: Response,
): Promise<{ message: string; isStructuredError: boolean }> => {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();

      // Check if this is new BaseResponse structure with errorCode
      if (errorData.success === false && errorData.errorCode) {
        // Import error handler dynamically to avoid circular dependencies
        const { getErrorMessage } = await import("../utils/errorHandler");
        const message = getErrorMessage(errorData.errorCode, errorData.params);
        return {
          message,
          isStructuredError: true,
        };
      }

      // Check if this is old structured API response with success: false and errorMessage
      if (errorData.success === false && errorData.errorMessage) {
        return {
          message: errorData.errorMessage,
          isStructuredError: true,
        };
      }

      // Try common error message fields for unstructured errors
      const message =
        errorData.message ||
        errorData.title ||
        errorData.detail ||
        errorData.error ||
        JSON.stringify(errorData); // Show full body if no known fields

      return { message, isStructuredError: false };
    } else {
      const textResponse = await response.text();
      // If body is empty, show status code with generic message
      const message = textResponse || `API Call Error (${response.status})`;
      return {
        message,
        isStructuredError: false,
      };
    }
  } catch {
    // Final fallback - just status code and generic message
    return {
      message: `API Call Error (${response.status})`,
      isStructuredError: false,
    };
  }
};

// Create an authenticated API client that uses centralized auth header provider
export const getAuthenticatedApiClient = (
  showErrorToasts: boolean = true,
): ApiClient => {
  const config = getConfig();

  // Create http object with custom fetch that includes authentication and error handling
  const authenticatedHttp = {
    fetch: async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
      const headers = await buildAuthHeaders(init);

      const response = await fetch(url, {
        ...init,
        headers,
        // Include credentials (cookies) for E2E test mode
        credentials: isE2ETestMode() ? "include" : "same-origin",
      });

      // A successful authenticated response means the token was accepted; reset the
      // sessionStorage counter so a future token expiry recovers from the silent path.
      // Only responses to requests that actually carried an Authorization header count:
      // [AllowAnonymous] endpoints (e.g. feature flags) return 200 even with a dead
      // session, and resetting on those pins the recovery ladder at the silent rung —
      // the exact infinite login loop the ladder exists to break.
      // Uses resetAuthRecoveryCounter (not clearAuthRecoveryState) to avoid resetting
      // redirectInFlight, which must stay true until the page reloads after a redirect.
      if (response.ok && headers["Authorization"]) {
        resetAuthRecoveryCounter();
      }

      // Handle 401 Unauthorized errors with automatic redirect
      if (response.status === 401) {
        console.log("🔐 401 Unauthorized detected - token expired or invalid");
        
        // Clear token cache to force fresh authentication
        clearTokenCache();
        
        // Trigger automatic login redirect if handler is available
        if (globalAuthRedirectHandler) {
          console.log("🔐 Triggering automatic login redirect...");
          globalAuthRedirectHandler();
        } else {
          console.warn("⚠️ No auth redirect handler available - user needs to refresh page");
        }
        
        // Continue with normal error handling (toast suppressed on /terminal routes)
      }

      // Global error handling with toast notifications
      // Handle both HTTP errors (!response.ok) and business logic errors (success: false)
      const shouldCheckForBusinessErrors = response.ok && showErrorToasts && globalToastHandler;
      const shouldHandleHttpErrors = !response.ok && showErrorToasts && globalToastHandler;
      
      if (shouldHandleHttpErrors || shouldCheckForBusinessErrors) {
        // Clone response to preserve it for SwaggerException
        const responseClone = response.clone();

        try {
          const errorInfo = await extractErrorMessage(responseClone);

          // Suppress toast on 409 when the backend returned a structured BaseResponse
          // (success: false + errorCode). These 409s are typed business outcomes (e.g.
          // "feedback already submitted") and the caller's hook handles them.
          // Unstructured 409s (non-JSON or missing errorCode) still surface as toasts.
          const suppressOn409 = response.status === 409 && errorInfo.isStructuredError;

          // Show toast for all errors - centralized handling
          console.log(
            `🔍 Error debug - isStructuredError: ${errorInfo.isStructuredError}, message: "${errorInfo.message}"`,
          );

          if (suppressOn409) {
            console.log(`🔇 Toast suppressed for typed 409 business outcome: ${errorInfo.message}`);
          } else if (errorInfo.isStructuredError && globalToastHandler) {
            // Structured API error - show ErrorMessage
            console.error(
              `🚨 Structured API Error [${response.status}] ${url}:`,
              errorInfo.message,
            );
            if (!isTerminalRoute()) globalToastHandler("Upozornění", errorInfo.message);
          } else if (shouldHandleHttpErrors && globalToastHandler) {
            // Only show unstructured errors for HTTP errors, not for business logic warnings
            const title = `Chyba API (${response.status})`;
            console.error(
              `🚨 Unstructured API Error [${response.status}] ${url}:`,
              errorInfo.message,
            );
            if (!isTerminalRoute()) globalToastHandler(title, errorInfo.message);
          }
        } catch (toastError) {
          console.error("🍞 Failed to show error toast:", toastError);
          // Fallback toast only for HTTP errors
          if (shouldHandleHttpErrors && globalToastHandler) {
            if (!isTerminalRoute()) globalToastHandler(
              `Chyba API (${response.status})`,
              "Neočekávaná chyba na serveru",
            );
          }
        }
      }

      return response;
    },
  };

  return new ApiClient(config.apiUrl, authenticatedHttp);
};

// Create an authenticated API client with custom token provider (for advanced scenarios)
export const getAuthenticatedApiClientWithProvider = (
  getAccessToken: () => Promise<string | null>,
): ApiClient => {
  const config = getConfig();

  const customHttp = {
    fetch: async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
      const token = await getAccessToken();

      const headers: Record<string, string> = {
        ...((init?.headers as Record<string, string>) || {}),
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      return fetch(url, {
        ...init,
        headers,
        // Include credentials (cookies) for E2E test mode
        credentials: isE2ETestMode() ? "include" : "same-origin",
      });
    },
  };

  return new ApiClient(config.apiUrl, customHttp);
};

/**
 * Get an authenticated fetch function with the same headers as the API client.
 * Returns a fetch-like function that automatically attaches auth headers.
 *
 * Key behaviors:
 * - Auth header ALWAYS wins: caller-supplied `Authorization` headers are overwritten by the helper's auth header.
 * - Does NOT throw on non-2xx response — the caller owns status-code branching.
 * - Does NOT trigger global error toasts or the 401 login redirect — those belong to `getAuthenticatedApiClient()`.
 *   Callers must handle error UX themselves.
 *
 * When to use this:
 * Prefer the typed generated client (`getAuthenticatedApiClient()`) for normal calls. Reach for this
 * helper only when an endpoint's success/business-outcome contract cannot yet be expressed through the
 * generated client — for example, an `If-Match`-based update returning HTTP 412 Precondition Failed
 * before the controller has been annotated with `[ProducesResponseType(StatusCodes.Status412PreconditionFailed)]`
 * and before the NSwag template knows to surface 412 as a typed branch. Once the contract is annotated
 * and the typed branch is generated, migrate the call site back to the generated client.
 */
export function getAuthenticatedFetch(): (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response> {
  return async (input, init = {}) => {
    const headers = await buildAuthHeaders(init);
    return fetch(input, {
      ...init,
      headers: { ...(init.headers ?? {}), ...headers },
      credentials: isE2ETestMode() ? "include" : "same-origin",
    });
  };
}

/**
 * Make a single GET request with the same authentication headers as the API client.
 * Use this for resources that must be fetched via JS (e.g. images behind auth).
 */
export const authenticatedFetch = async (url: string): Promise<Response> => {
  const authHeader = await getAuthHeader();
  const headers: Record<string, string> = {};
  if (isE2ETestMode()) {
    const e2eToken = getE2EAccessToken();
    if (e2eToken) headers["X-E2E-Test-Token"] = e2eToken;
  } else if (authHeader) {
    headers["Authorization"] = authHeader;
  }
  return fetch(url, {
    headers,
    credentials: isE2ETestMode() ? "include" : "same-origin",
  });
};

// Legacy export for backward compatibility
export { getApiClient as apiClient };

// API client configuration
export const getApiConfig = () => {
  const config = getConfig();
  return {
    baseUrl: config.apiUrl,
    timeout: 30000, // 30 seconds
  };
};

// Query keys for TanStack Query
export const QUERY_KEYS = {
  weather: ["weather"] as const,
  weatherForecast: ["weatherForecast"] as const,
  catalog: ["catalog"] as const,
  productMargins: ["productMargins"] as const,
  productMarginSummary: ["productMarginSummary"] as const,
  pricingBaseline: ["pricing-baseline"] as const,
  pricingScenarios: ["pricing-scenarios"] as const,
  financialOverview: ["financialOverview"] as const,
  financialComparison: ["financialComparison"] as const,
  journal: ["journal"] as const,
  marketingCalendar: ["marketing-calendar"] as const,
  photobank: ["photobank"] as const,
  transportBox: ["transport-boxes"] as const,
  manufactureOutput: ["manufacture-output"] as const,
  manufactureDifficulty: ["manufacture-difficulty-settings"] as const,
  manufactureOrders: ["manufacture-orders"] as const,
  productUsage: ["product-usage"] as const,
  health: ["health"] as const,
  giftPackages: ["gift-packages"] as const,
  warehouseStatistics: ["warehouse-statistics"] as const,
  stockTaking: ["stock-taking"] as const,
  userManagement: ["user-management"] as const,
  invoiceImportStatistics: ["invoice-import-statistics"] as const,
  bankStatements: ["bank-statements"] as const,
  backgroundRefresh: ["background-refresh"] as const,
  issuedInvoices: ["issued-invoices"] as const,
  invoices: ["invoices"] as const,
  dashboard: ["dashboard"] as const,
  orgChart: ["orgChart"] as const,
  departments: ["departments"] as const,
  stockUpOperations: ["stock-up-operations"] as const,
  recurringJobs: ["recurring-jobs"] as const,
  knowledgeBase: ["knowledge-base"] as const,
  leaflet: ["leaflet"] as const,
  expeditionListArchive: ["expedition-list-archive"] as const,
  dataQuality: ["data-quality"] as const,
  articles: ["articles"] as const,
  articleTrace: ["article-trace"] as const,
  smartsupp: ["smartsupp"] as const,
  manufacturedProductInventory: ["manufactured-product-inventory"] as const,
  meetingTasks: ["meetingTasks"] as const,
  packingOrder: ["packingOrder"] as const,
  orderTrackingNumber: ["order-tracking-number"] as const,
  orderTrackingNumbers: ["order-tracking-numbers"] as const,
  shipmentLabels: ["shipmentLabels"] as const,
  featureFlags: ["feature-flags"] as const,
  catalogDocuments: ["catalog-documents"] as const,
  materialContainers: ["materialContainers"] as const,
  suppliers: ["suppliers"] as const,
  overtime: ["overtime"] as const,
  marketingPerformanceMonths: ["marketing-performance", "months"] as const,
  marketingPerformanceComparison: ["marketing-performance", "comparison"] as const,
  // Add more query keys as needed
  // users: ['users'] as const,
  // products: ['products'] as const,
} as const;

// Default query options
export const DEFAULT_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
  retry: 3,
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000),
};
